import { Component, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  RegisteredService,
  CreatedRegisteredParkerRequest,
  ParkerService,
  LicensePlateService,
  ParkingTypeService,
  ListLongTermParkersRequest,
  CreateLongTermParkersRequest,
  ParkingRightService,
  CreateParkingRightRequest,
  TimeBasedParkingRightDto,
  ParkingRightTypeEnum,
} from '../../../api/gen';
import {
  MAX_PLATE_LENGTH,
  dateInputValueToIso,
  isValidPlateFormat,
  normalizePlate,
  sanitizePlateInput,
  todayAsDateInputValue,
} from 'shared-lib';
import { describeHttpError } from '../http-error';
import { DateFieldComponent } from '../date-field/date-field.component';

/** Mirrors `UsersPolicy` (FirstNameMaxLength, LastNameMaxLength, EmailMaxLength). */
const NAME_MAX_LENGTH = 100;
const EMAIL_MAX_LENGTH = 100;
const PHONE_MAX_LENGTH = 30;
/** Mirrors the ParkingRight.Name column (HasMaxLength 256). */
const RIGHT_NAME_MAX_LENGTH = 256;
// Keycloak refuses an address without a domain ("error-invalid-email"), which only surfaced as a
// failed registration after the plates were already registered. Checked here, before anything is sent.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
const PHONE_PATTERN = /^\+?[0-9][0-9\s\-/()]{5,}$/;

/** A field's blocking problem: a translation key plus its interpolation params. */
interface FieldError {
  key: string;
  params?: Record<string, unknown>;
}

/**
 * Registers one long-term parker (Dauerparker) at a site: a person, their plates, and a parking right
 * with a contract period.
 *
 * The company & members form next to it is built around organizations — company search, membership,
 * AK number, Supplier/Employee/Student — which is how JKU credentials its people. A site that sells
 * individual long-term contracts has none of that, so this form asks only for the person and the period.
 *
 * Two rules the form enforces, both because the right is what opens the site to the plate:
 * <ul>
 *   <li><b>"Valid until" is required.</b> A right without an end date never expires, so a forgotten
 *   date is a car that parks free for good. The operator has to name the end of the contract.</li>
 *   <li><b>"Valid from" may lie in the future.</b> Until then the plate is an ordinary short-term
 *   parker, which is how an operator enters a contract now that only starts once it is paid.</li>
 * </ul>
 * No site classification is written: a long-term parker is entitled by the right, not by a class.
 */
@Component({
  selector: 'app-long-term-registration',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatProgressSpinnerModule, TranslatePipe, DateFieldComponent],
  templateUrl: './long-term-registration.component.html',
})
export class LongTermRegistrationComponent {
  /** The site (parking space) the right is created for. */
  parkingSpaceId = input<string | null>(null);
  /** The logged-in operator id — attributed to the parking type and the right. */
  operatorId = input<string | null>(null);
  /**
   * The site's plate cap (`ParkingSpace.MaxSelfServiceLicensePlates`), the same one the parker app
   * applies: at the cap, no further plate can be added. Null = no limit.
   */
  maxPlates = input<number | null>(null);

  /** Emitted after a successful registration so the parent can refresh its registry. */
  registered = output<void>();

  private readonly registeredService = inject(RegisteredService);
  private readonly parkerService = inject(ParkerService);
  private readonly plateApi = inject(LicensePlateService);
  private readonly parkingTypeService = inject(ParkingTypeService);
  private readonly parkingRightService = inject(ParkingRightService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly t = inject(TranslateService);

  readonly maxPlateLength = MAX_PLATE_LENGTH;
  readonly nameMaxLength = NAME_MAX_LENGTH;
  readonly emailMaxLength = EMAIL_MAX_LENGTH;
  readonly phoneMaxLength = PHONE_MAX_LENGTH;
  readonly rightNameMaxLength = RIGHT_NAME_MAX_LENGTH;
  readonly today = todayAsDateInputValue();

  firstName = signal('');
  lastName = signal('');
  /** Optional. Becomes the right's name, which the registry shows as the company column. */
  company = signal('');
  email = signal('');
  phone = signal('');
  plates = signal<string[]>(['']);
  validFrom = signal('');
  validTo = signal('');
  comment = signal('');

  isSubmitting = signal(false);
  /** Set by the first submit attempt: from then on, missing required fields are flagged inline too. */
  submitAttempted = signal(false);

  // ---- Validation -----------------------------------------------------------
  // Format errors show as soon as a value is typed; "required" errors only after a submit attempt.
  readonly firstNameError = computed<FieldError | null>(() => this.tooLong(this.firstName(), NAME_MAX_LENGTH));

  readonly lastNameError = computed<FieldError | null>(() => {
    if (!this.hasName()) return this.submitAttempted() ? { key: 'hotline.longTerm.err.name' } : null;
    return this.tooLong(this.lastName(), NAME_MAX_LENGTH);
  });

  readonly companyError = computed<FieldError | null>(() => this.tooLong(this.company(), RIGHT_NAME_MAX_LENGTH));

  readonly emailError = computed<FieldError | null>(() => {
    const email = this.email().trim();
    if (!email) return null;
    if (email.length > EMAIL_MAX_LENGTH || !EMAIL_PATTERN.test(email)) return { key: 'hotline.longTerm.err.email' };
    return null;
  });

  readonly phoneError = computed<FieldError | null>(() => {
    const phone = this.phone().trim();
    if (!phone) return null;
    if (phone.length > PHONE_MAX_LENGTH || !PHONE_PATTERN.test(phone)) return { key: 'hotline.longTerm.err.phone' };
    return null;
  });

  /** One entry per plate row: the row's error, or null. An empty row is ignored, not flagged. */
  readonly plateErrors = computed<(FieldError | null)[]>(() =>
    this.plates().map(p => (p && !isValidPlateFormat(p) ? { key: 'hotline.longTerm.err.plateFormat' } : null)));

  readonly platesError = computed<FieldError | null>(() =>
    this.submitAttempted() && this.cleanPlates().length === 0 ? { key: 'hotline.longTerm.err.plate' } : null);

  readonly validToError = computed<FieldError | null>(() => {
    if (!this.validTo()) return this.submitAttempted() ? { key: 'hotline.longTerm.err.validTo' } : null;
    if (this.validTo() < this.today) return { key: 'hotline.longTerm.err.validToPast' };
    // ISO dates compare correctly as strings. The backend refuses from >= to, so say it here first.
    if (this.validFrom() && this.validFrom() >= this.validTo()) return { key: 'hotline.longTerm.err.range' };
    return null;
  });

  /** The form's first blocking problem, or null when it can be submitted. Independent of submitAttempted. */
  readonly problem = computed<FieldError | null>(() => {
    if (!this.hasName()) return { key: 'hotline.longTerm.err.name' };
    if (this.cleanPlates().length === 0) return { key: 'hotline.longTerm.err.plate' };
    if (!this.validTo()) return { key: 'hotline.longTerm.err.validTo' };
    return this.firstNameError() ?? this.lastNameError() ?? this.companyError() ?? this.emailError() ?? this.phoneError()
      ?? this.plateErrors().find(e => e !== null) ?? this.validToError();
  });

  /** A last name or a company — the registry needs one of them to label the row. */
  private hasName(): boolean {
    return !!this.lastName().trim() || !!this.company().trim();
  }

  private tooLong(value: string, max: number): FieldError | null {
    return value.trim().length > max ? { key: 'hotline.longTerm.err.tooLong', params: { max } } : null;
  }

  // ---- Plates ---------------------------------------------------------------
  /** Normalised, de-duplicated plates — what is actually registered. */
  private cleanPlates(): string[] {
    return [...new Set(this.plates().map(p => normalizePlate(p)).filter(p => p.length > 0))];
  }

  onPlateInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value;
    const cleaned = sanitizePlateInput(raw);
    this.plates.update(list => list.map((p, i) => (i === index ? cleaned : p)));
    if (cleaned === raw) return;

    const caret = input.selectionStart ?? raw.length;
    const restored = sanitizePlateInput(raw.slice(0, caret)).length;
    input.value = cleaned;
    input.setSelectionRange(restored, restored);
  }

  /** True once the plate rows reach the site's cap — the "Add plate" button is hidden then. */
  readonly atPlateLimit = computed(() => {
    const max = this.maxPlates();
    return max != null && max > 0 && this.plates().length >= max;
  });

  addPlate(): void {
    if (this.atPlateLimit()) return;
    this.plates.update(list => [...list, '']);
  }

  removePlate(index: number): void {
    this.plates.update(list => (list.length > 1 ? list.filter((_, i) => i !== index) : ['']));
  }

  // ---- Submit ---------------------------------------------------------------
  async submit(): Promise<void> {
    const spaceId = this.parkingSpaceId();
    if (!spaceId || this.isSubmitting()) return;

    this.submitAttempted.set(true);
    const problem = this.problem();
    if (problem) {
      this.toast(this.t.instant(problem.key, problem.params));
      return;
    }

    const plates = this.cleanPlates();
    const operatorId = this.operatorId();

    this.isSubmitting.set(true);
    try {
      // 1) The person: reuse the parker this email already belongs to, else create one. Resolved
      // before anything is written, so a refusal below leaves nothing half-registered.
      const existingParkerId = await this.findParkerByEmail(this.email().trim().toLowerCase());
      if (existingParkerId && await this.hasRightAtSite(existingParkerId, spaceId)) {
        this.toast(this.t.instant('hotline.longTerm.err.alreadyHasRight'));
        return;
      }

      // 2) Register the plates and refuse any already held by another registered parker: linking it
      // here too would make the gate resolve that car to one of two people.
      const plateIds: string[] = [];
      const alreadyLinked = new Set<string>();
      for (const plate of plates) {
        const licensePlate = await firstValueFrom(this.parkerService.parkerRegisterLicensePlatePost({ fullNumber: plate }));
        if (!licensePlate?.id) throw new Error(`License plate registration returned no id for ${plate}.`);

        const ownership = await firstValueFrom(this.plateApi.operatorLicensePlateLicensePlateIdOwnershipGet(licensePlate.id));
        const other = (ownership?.holders ?? []).find(h => h.isRegistered && h.parkerId !== existingParkerId);
        if (other) {
          const owner = [other.firstName, other.lastName].filter(Boolean).join(' ') || other.email || '';
          this.toast(this.t.instant('hotline.msg.plateOwnedByOther', { plate, owner }));
          return;
        }

        plateIds.push(licensePlate.id);
        if (existingParkerId && (licensePlate.parkersIDs ?? []).includes(existingParkerId)) {
          alreadyLinked.add(licensePlate.id);
        }
      }

      // 3) Create the parker when the email did not name one.
      const parkerId = existingParkerId ?? await this.createParker(plates[0]);

      // 4) Link the plates to the parker — the gate resolves a detected plate through this link.
      for (const plateId of plateIds) {
        if (alreadyLinked.has(plateId)) continue;
        await firstValueFrom(
          this.registeredService.operatorRegisteredParkerRegisteredParkerIdAssignLicensePlateLicensePlateIdPost(parkerId, plateId));
      }

      // 5) The right itself, on the site's long-term parking type.
      const parkingTypeId = await this.ensureLongTermType(spaceId, operatorId);
      const typeDto: TimeBasedParkingRightDto = {
        type: ParkingRightTypeEnum.TimeBased,
        validFrom: dateInputValueToIso(this.validFrom()),
        validTo: dateInputValueToIso(this.validTo()),
        startTime: null,
        endTime: null,
        validDays: null,
        isAllDay: true,
        isRecurring: false,
      };
      const request: CreateParkingRightRequest & { operatorId?: string | null } = {
        typeDto,
        parkingTypeId,
        allowedParkingSpaces: [spaceId],
        allowedLicensePlates: plateIds,
        // Space-wide: the zone a car parks in is still recorded on its stay.
        allowedParkingZones: null,
        purchasedByParker: parkerId,
        purchasedByOrganizationId: null,
        paymentReferenceId: null,
        name: this.company().trim() || this.fullName(),
        comment: this.buildComment(),
        operatorId,
      };
      await firstValueFrom(this.parkingRightService.parkingRightsPost(request));

      this.toast(this.t.instant('hotline.msg.registered'));
      this.resetForm();
      this.registered.emit();
    } catch (e) {
      console.error('Long-term registration failed:', e);
      this.toast(`${this.t.instant('hotline.msg.registerFailed')} ${describeHttpError(e)}`);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  /**
   * The parker an email already belongs to, or null.
   *
   * The backend filters email with Contains, so the hits are compared exactly — taking the first one
   * would attach this contract to "max@example.com.invalid" when "max@example.com" was typed. A failed
   * lookup does not block: `CreateRegisteredParker` refuses a duplicate address server-side anyway.
   */
  private async findParkerByEmail(email: string): Promise<string | null> {
    if (!email) return null;
    try {
      const existing = await firstValueFrom(
        this.registeredService.operatorRegisteredParkerFilterPost({ email, pageIndex: 0, pageSize: 20, includeDeleted: false }));
      return (existing?.items ?? []).find(p => (p.email ?? '').trim().toLowerCase() === email)?.id ?? null;
    } catch (e) {
      console.error(`Could not check whether ${email} is already registered:`, e);
      return null;
    }
  }

  /**
   * Whether the parker already holds a live right at this site. One person gets one right per site;
   * a second contract or plate belongs on the existing right (Edit in the registry), not beside it.
   */
  private async hasRightAtSite(parkerId: string, spaceId: string): Promise<boolean> {
    const rights = await firstValueFrom(this.parkingRightService.parkingRightsByParkerParkerIdGet(parkerId, false, true));
    return (rights ?? []).some(r =>
      !r.isDeleted && (r.allowedParkingSpaces ?? []).some(s => s.parkingSpaceId === spaceId));
  }

  /**
   * Creates the registered parker. Without a known email a placeholder is generated on an internal
   * domain, as the company form does; the operator corrects it later via Edit once the real address
   * is known — the person's own login is matched on it.
   */
  private async createParker(firstPlate: string): Promise<string> {
    // A company-only contract has no person: the account carries the company name instead.
    const lastName = this.lastName().trim() || this.company().trim().slice(0, NAME_MAX_LENGTH);
    const request: CreatedRegisteredParkerRequest = {
      email: this.email().trim() || this.buildPlaceholderEmail(firstPlate),
      firstName: this.firstName().trim() || lastName,
      lastName,
      defaultPassword: this.generatePassword(),
      credits: [],
    };
    const parker = await firstValueFrom(this.registeredService.operatorRegisteredParkerPost(request));
    if (!parker?.id) throw new Error('Registered parker creation returned no id.');
    return parker.id;
  }

  /** Find-or-create the site's long-term parking type, owned by the acting operator. */
  private async ensureLongTermType(spaceId: string, operatorId: string | null): Promise<string> {
    const listRequest: ListLongTermParkersRequest = {
      availableForParkingSpaces: [spaceId],
      operatorId: operatorId ?? undefined,
      includeDeleted: false,
      pageIndex: 0,
      pageSize: 1,
    };
    const existing = await firstValueFrom(this.parkingTypeService.parkingTypesLongTermParkersFilterPost(listRequest));
    const existingTypeId = existing?.items?.at(0)?.parkingTypeId;
    if (existingTypeId) return existingTypeId;

    const createRequest: CreateLongTermParkersRequest = { availableForParkingSpaces: [spaceId] };
    const created = await firstValueFrom(this.parkingTypeService.parkingTypesLongTermParkersPost(operatorId ?? '', createRequest));
    const createdTypeId = created?.parkingTypeId;
    if (!createdTypeId) throw new Error('Long-term parking type creation returned no parkingTypeId.');
    return createdTypeId;
  }

  // ---- Helpers --------------------------------------------------------------
  private fullName(): string {
    return [this.firstName().trim(), this.lastName().trim()].filter(Boolean).join(' ');
  }

  private buildComment(): string | null {
    const parts: string[] = [];
    if (this.phone().trim()) parts.push(`Tel: ${this.phone().trim()}`);
    if (this.comment().trim()) parts.push(this.comment().trim());
    return parts.length ? parts.join(' · ') : null;
  }

  private buildPlaceholderEmail(plate: string): string {
    const plateSlug = plate.toLowerCase().replace(/[^a-z0-9]/g, '') || 'plate';
    return `${plateSlug}.longterm@parker.jjames.local`;
  }

  private generatePassword(): string {
    // Random temp password; long-term parkers registered here do not log in with it.
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const rand = crypto.getRandomValues(new Uint32Array(16));
    return 'Jj1!' + Array.from(rand, n => chars[n % chars.length]).join('');
  }

  private resetForm(): void {
    this.firstName.set('');
    this.lastName.set('');
    this.email.set('');
    this.phone.set('');
    this.plates.set(['']);
    this.validFrom.set('');
    this.validTo.set('');
    this.comment.set('');
    this.company.set('');
    this.submitAttempted.set(false);
  }

  private toast(message: string): void {
    this.snackBar.open(message, 'Close', { duration: 4000 });
  }
}
